import { useEffect, useMemo } from 'react'
import { DateTime } from 'luxon'
import { Divider, Icon } from 'semantic-ui-react'
import { NavButtons } from '../../end-of-year-review/nav-buttons'
import { fetchUserDocuments } from '@/features/user/your-practice/documents/lib/userDocuments.slice'

import { Text } from '@/componentsV1/BaseComponents'
import { fetchFilteredUserTransactions } from '@/features/transactions/transactions.slice'
import { useReselector, useFetchResponse } from '@/utils/sharedHooks'
import { useNavigate, useParams } from 'react-router-dom'
import { selectUserDocumentList } from '@/features/user/your-practice/documents/lib/userDocuments.selector'
import { getFinancialProfile } from '@/selectors/user.selectors'
import { TAX_ENTITY_TYPES } from '@/features/taxes/taxConstants'
import { getAllProviders } from '@/features/provider/provider.selectors'
import { userGetProcessingFees } from '../../end-of-year-review/actions'
import { ReviewDocuments } from '../../end-of-year-review/review-document-uploads'
import { useAppDispatch } from '@/utils/typeHelpers'
import { fetchPayrollProfileIfNeeded } from '@/features/payroll/payrollActions'
import { fetchPlaidItemsIfNeeded } from '@/actions/plaidItemActions'
import { fetchTransactionCategoriesIfNeeded } from '@/features/reports/reports.slice'
import { fetchFinancialAccountsIfNeeded } from '@/actions/financialAccountActions'
import { fetchProvidersIfNeeded } from '@/features/provider/provider.actions'
import './styles.scss'

export const FollowUpOnDocuments = () => {
  const { year } = useParams()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  useEffect(() => {
    const fetch = async () => {
      await dispatch(fetchFinancialAccountsIfNeeded())
      await dispatch(fetchPlaidItemsIfNeeded())
    }

    dispatch(fetchTransactionCategoriesIfNeeded())
    dispatch(fetchPayrollProfileIfNeeded())
    dispatch(fetchUserDocuments())
    dispatch(fetchProvidersIfNeeded({ alwaysFetch: true }))

    dispatch(
      fetchFilteredUserTransactions({
        startDate: DateTime.fromObject(
          { year: parseInt(year ?? '0') },
          { zone: 'utc' }
        ).startOf('year'),
        endDate: DateTime.fromObject(
          { year: parseInt(year ?? '0') },
          { zone: 'utc' }
        ).endOf('year'),
      })
    )

    fetch()
  }, [dispatch, year])

  const documents = useReselector(selectUserDocumentList)
  const financialProfile = useReselector(getFinancialProfile)
  const providers = useReselector(getAllProviders)

  const isMultiYearSCorp = useMemo(() => {
    if (!financialProfile) return false
    const { taxEntityType, scorpVerifiedAt, entityChangeElectionDate } =
      financialProfile
    if (
      taxEntityType !== TAX_ENTITY_TYPES.form_1120_s ||
      !scorpVerifiedAt ||
      !entityChangeElectionDate
    )
      return false
    const startOfYear = DateTime.fromObject(
      { year: parseInt(year ?? '0'), month: 1, day: 1 },
      { zone: 'utc' }
    )
    const electionDate = DateTime.fromISO(entityChangeElectionDate, {
      zone: 'utc',
    })
    return electionDate < startOfYear
  }, [financialProfile, year])

  const processingFees = useFetchResponse(userGetProcessingFees, {
    year: year ?? '',
    transactionCategoryIdentifiers: [],
    includeProviders: true,
  })

  return (
    <div className="eoy-review_wrapper">
      <header className="header">
        <Text>{year} End of Year Review</Text>
        <div className="autosave-wrapper">
          <Icon name="check" style={{ color: '#457634' }} />
          <Text style={{ color: '#457634' }}>Autosaved</Text>
        </div>
      </header>

      <Divider style={{ marginTop: 50 }} />
      <div className="step-body">
        <ReviewDocuments
          documents={documents}
          taxYear={year ?? ''}
          isMultiYearSCorp={isMultiYearSCorp}
          providers={providers}
          processingFees={processingFees}
          setIsStepComplete={() => {}}
          isFollowUp
        />
      </div>

      <NavButtons
        isStepComplete
        stepBack={() => {
          navigate(`/taxes/annual/review-and-close-books/${year}/follow-up`)
        }}
        stepForward={() => {
          navigate(`/taxes/annual/review-and-close-books/${year}/follow-up`)
        }}
        saveAndExit={() => {
          navigate('/taxes/annual')
        }}
      />
    </div>
  )
}
